import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { Router } from 'react-router-dom';
import App from 'App';
import { setLoggedIn, setToken, SetLoggedInAsSupervisor, SetLoggedInAsAdmin, setPage, resetLogin } from './redux/login';
import history from './config/history';
import ReactGA from 'react-ga';
import { persistStore } from 'redux-persist';
import { PersistGate } from 'redux-persist/lib/integration/react';
import * as Sentry from '@sentry/browser';
import * as FullStory from '@fullstory/browser';
import { QueryClientProvider } from '@tanstack/react-query';
import ReduxToastr from 'react-redux-toastr';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import mixpanel from 'mixpanel-browser';

import * as http from './utils/http';
import { withFeatureFlagProvider } from './crn-feature-flags';

// CSS
import 'node_modules/react-toggle-switch/dist/css/switch.min.css';
import 'foundation-sites/dist/css/foundation.min.css';
import './assets/css/materialize.min.css';
import './assets/css/custom.css';
import './assets/css/onboarding.css';
import './assets/css/nurse-dashboard.css';
import 'fullcalendar/dist/fullcalendar.print.css';
import 'fullcalendar/dist/fullcalendar.min.css';
import './assets/css/foundation-icons.css';
import './assets/css/jquery-ui.theme.css';
import './assets/css/jquery-ui.structure.css';
import 'font-awesome/css/font-awesome.min.css';
import './assets/css/Admin/admin.css';
import './assets/css/loyalty-rewards.css';
import './assets/css/loyalty-history.css';
import './assets/css/open-shift.css';
import './assets/css/error-reporting.css';
import './assets/css/assign-shift.css';
import './assets/css/nps-modal.css';
import './assets/css/multi-select.css';
import './assets/css/user-feature-settings.css';
import './assets/css/help-center.css';
import './assets/css/Bonus/bonus-table.css';
import './assets/css/helper.css';
import './assets/css/customForm.css';
import './assets/css/Bonus/loader.css';
import './assets/css/Supervisor/Nurse/nurse-table.css';
import './assets/css/Supervisor/Nurse/profile.css';
import './assets/css/Supervisor/new-supervisor-timesheet.css';

// JS
import 'script-loader!jquery';
import 'script-loader!foundation-sites';
import 'script-loader!moment'; // TODO: remove as last step for DEV-6617
import 'script-loader!jquery-ui/ui/widgets/datepicker.js';
import 'script-loader!fullcalendar/dist/fullcalendar.min.js';
import 'script-loader!jquery-serializejson/jquery.serializejson.min.js';
import 'script-loader!jquery-placeholder';

// CSS
import './assets/css/supervisor-shift.css';
import 'rc-time-picker/assets/index.css';
import './assets/css/Admin/organization.css';
import './assets/css/Admin/requests.css';
import './assets/css/Admin/timesheet.css';
import './assets/css/Admin/requirement.css';
import './assets/css/Admin/user.css';
import './assets/css/Admin/dashboard.css';
import './assets/css/fonts.css';
import './assets/css/add-new-shift.css';
import './assets/css/custom-accordion.css';
import './assets/css/materialize.min.css';
import './assets/css/supervisor-dashboard.css';
import './assets/css/nurse-account.css';
import './assets/css/nurse-cico.css';
import './assets/css/progress-bar.css';
import './assets/css/registration-complete.css';
import './assets/css/registration-step-1.css';
import './assets/css/registration-step-3.css';
import './assets/css/registration-step-4.css';
import './assets/css/shift-detail.css';
import './assets/css/super-timesheet-detail.css';
import './assets/css/supervisor-alert.css';
import './assets/css/supervisor-cico.css';
import './assets/css/terms.css';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import './assets/css/Admin/bonus.css';
import './assets/css/multi-date-picker.css';
import './assets/css/common.css';
import './assets/css/Admin/announcement-table.css';
import 'react-redux-toastr/lib/css/react-redux-toastr.min.css';
import './assets/css/custom-toastr.css';
import './assets/css/DesignSystem/index.css';
import { configureApp } from './config';
import { clearLocalStorage } from './utils/user';
import { CFBreakpointProvider, CFModalProvider, CFToastProvider } from '@connectrn/ds-ui-web';
import { queryClient } from './client/queryClient';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

const store = configureApp(process.env.BASEURL, process.env.REDUX_DEBUG);
const persistor = persistStore(store);

$('input, textarea').placeholder();

$(document).foundation();

if (localStorage.token) {
	if (localStorage.role == 'supervisor') {
		store.dispatch(SetLoggedInAsSupervisor(true));
	}
	if (localStorage.role == 'admin') {
		store.dispatch(SetLoggedInAsAdmin(true));
	}
	store.dispatch(setLoggedIn(true));
	store.dispatch(setToken(localStorage.token));
}

history.listen((location) => {
	ReactGA.set({ page: location.pathname });
	ReactGA.pageview(location.pathname);
});

window.addEventListener('storage', (event) => {
	if (event.key == 'logout-event') {
		clearLocalStorage();
		store.dispatch(resetLogin());
		store.dispatch(setLoggedIn(false));
		store.dispatch(setPage('/'));
		store.dispatch(setToken(''));
	}
});

// Resetting previous logged in tokens while logging in with new token.
window.addEventListener('storage', (event) => {
	if (event.key === 'login-event') {
		clearLocalStorage();
		store.dispatch(resetLogin());
		store.dispatch(setLoggedIn(false));
		store.dispatch(setPage('/'));
		store.dispatch(setToken(''));
	}
});

// mixpanel initialization
mixpanel.init(process.env.MIXPANEL_KEY);

// FullStory initialization
FullStory.init({ devMode: process.env.APPENV !== 'prod', orgId: '9SGNC' });

// HTTP initialization
http.initInterceptors();

if (process.env.APPENV === 'prod') {
	Sentry.init({
		beforeSend(event) {
			event.breadcrumbs = [];
			return event;
		},
		dsn: 'https://2729250ab84c4e278ba41142a3076c02@sentry.io/4470109',
		environment: process.env.APPENV
	});
}

const AppWithProviders = () => (
	<Provider store={store}>
		<PersistGate loading={null} persistor={persistor}>
			<QueryClientProvider client={queryClient}>
				<CFBreakpointProvider>
					<CFToastProvider bottomOffset={24}>
						<CFModalProvider footerSafeGap={24}>
							<Router history={history}>
								<div style={{ height: '100%' }}>
									<ReduxToastr
										closeOnToastrClick
										newestOnTop={false}
										position="top-right"
										preventDuplicates
										progressBar
										timeOut={4000}
										transitionIn="fadeIn"
										transitionOut="fadeOut"
									/>
									<App />
								</div>
							</Router>
						</CFModalProvider>
					</CFToastProvider>
				</CFBreakpointProvider>
			</QueryClientProvider>
		</PersistGate>
	</Provider>
);

async function bootApp() {
	const FeatureFlagProvider = await withFeatureFlagProvider(process.env.LAUNCHDARKLY_TOKEN);

	ReactDOM.render(
		<FeatureFlagProvider>
			<AppWithProviders />
		</FeatureFlagProvider>,
		document.getElementById('app')
	);
}

bootApp();
